import { tokenize } from './tokenize';

// Tracks the species each input / output had when the balance started
// referring to it, so a later species change can be flagged. Nothing else
// catches this: a species swap with the same measure (Hydrogen → Jet Fuel,
// both kg) passes every unit check while the balance keeps computing
// hydrogen physics under the new label.
//
// The baseline is positional — `{ inputs: [species], outputs: [species] }`
// aligned with the form rows — rather than keyed by react-hook-form field
// ids, because the AI bridge replaces whole arrays (regenerating ids) and
// its species changes need flagging too. Row removal has to be mirrored with
// `removeBaselineRow`, and an AI rewrite with `realignBaselineRows`, to keep
// the alignment.

export const EMPTY_SPECIES_BASELINE = { inputs: [], outputs: [] };

const KINDS = ['inputs', 'outputs'];

// Identifiers the balance actually uses; comments and string literals don't
// count.
export function balanceIdentifiers(balance) {
  return new Set(
    tokenize(balance || '')
      .filter(t => t.type === 'identifier')
      .map(t => t.value),
  );
}

// `rows` is `{ inputs: { species, slugs }, outputs: { species, slugs } }`.
// A row gets its baseline the first time the balance references its slug and
// keeps it until acknowledged. Returns `baseline` itself when nothing changed
// so a caller can store the result as state during render without looping.
export function captureSpeciesBaseline(baseline, rows, referenced) {
  let changed = false;
  const next = {};
  KINDS.forEach(kind => {
    const prev = baseline[kind] ?? [];
    const { species, slugs } = rows[kind];
    const updated = species.map((s, idx) => prev[idx] ?? (referenced.has(slugs[idx]) ? s : undefined));
    if (updated.length !== prev.length || updated.some((s, idx) => s !== prev[idx])) {
      changed = true;
    }
    next[kind] = updated;
  });
  return changed ? next : baseline;
}

export function speciesChanges(baseline, rows) {
  const changes = [];
  KINDS.forEach(kind => {
    rows[kind].species.forEach((species, idx) => {
      const from = baseline[kind]?.[idx];
      if (from && from !== species) changes.push({ kind, idx, from, to: species });
    });
  });
  return changes;
}

export function removeBaselineRow(baseline, kind, idx) {
  return { ...baseline, [kind]: (baseline[kind] ?? []).filter((_, i) => i !== idx) };
}

// A list the AI bridge rewrote can have rows inserted, removed or moved
// anywhere. Each row keeps its baseline by slug so the rest stay aligned;
// a slug the old list didn't have starts without one.
export function realignBaselineRows(baseline, kind, fromSlugs, toSlugs) {
  const prev = baseline[kind] ?? [];
  const bySlug = new Map(fromSlugs.map((slug, idx) => [slug, prev[idx]]));
  return { ...baseline, [kind]: toSlugs.map(slug => bySlug.get(slug)) };
}

export function acknowledgeSpecies(baseline, kind, idx, species) {
  const updated = [...(baseline[kind] ?? [])];
  updated[idx] = species;
  return { ...baseline, [kind]: updated };
}
